import { Component, useState } from "react";
import { Loader2, Send, Upload, X, FileText, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { serviceOptions, regionHubs } from "../data/site";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const MAX_BYTES = 6 * 1024 * 1024;
const MAX_FILES = 8;
const ERROR_TEXT = "Anfrage konnte nicht gesendet werden. Bitte anrufen: 0151 70004440 oder WhatsApp nutzen.";

const initialForm = (defaultService) => ({
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  postal_code: "",
  region: "",
  service: defaultService,
  object_type: "",
  area: "",
  floor: "",
  elevator: "",
  timeframe: "",
  callback_time: "",
  outside_region: false,
  message: "",
  consent: false,
  hp_field: "", // Honeypot (Feldname absichtlich ohne Autofill-Bezug wie "website"/"url")
});

// Fängt Render-Fehler ab, damit niemals eine leere/schwarze Seite erscheint
class FormErrorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="rounded-sm border border-red-300 bg-red-50 p-5 text-base text-red-800" role="alert" data-testid="cf-crash-fallback">
          {ERROR_TEXT}
        </div>
      );
    }
    return this.props.children;
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const inputCls =
  "w-full rounded-sm border border-transparent bg-light-gray px-4 py-3 text-base text-brand-black outline-none transition-all placeholder:text-mid-gray/70 focus:border-yellow focus:bg-white";
const labelCls = "mb-2 block text-sm font-semibold text-anthracite";

export default function ContactForm(props) {
  return (
    <FormErrorBoundary>
      <ContactFormInner {...props} />
    </FormErrorBoundary>
  );
}

function ContactFormInner({ variant = "private", defaultService = "" }) {
  const isBusiness = variant === "business";
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [result, setResult] = useState(null); // null | { ok: true, number } | { ok: false, message }
  const [form, setForm] = useState(() => initialForm(defaultService));

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const resetForm = () => {
    setForm(initialForm(defaultService));
    setFiles([]);
    setResult(null);
  };

  const onFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of picked) {
      if (files.length >= MAX_FILES) {
        toast.error(`Maximal ${MAX_FILES} Dateien.`);
        break;
      }
      if (!ALLOWED.includes(file.type)) {
        toast.error("Nur JPG, PNG, WEBP oder PDF erlaubt.");
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} ist zu groß (max. 6 MB).`);
        continue;
      }
      try {
        const base64 = await fileToBase64(file);
        setFiles((prev) => [...prev, { name: file.name, mime: file.type, base64, isPdf: file.type === "application/pdf" }]);
      } catch {
        toast.error("Datei konnte nicht gelesen werden.");
      }
    }
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setResult({ ok: false, message: "Bitte bestätigen Sie die Datenschutzhinweise." });
      return;
    }
    if (!form.email && !form.phone) {
      setResult({ ok: false, message: "Bitte geben Sie eine E-Mail oder Telefonnummer an." });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const endpoint = isBusiness ? "/business-inquiry" : "/contact";
      const res = await api.post(endpoint, {
        name: form.name,
        company: form.company || null,
        email: form.email || null,
        phone: form.phone || null,
        location: form.location,
        postal_code: form.postal_code || null,
        region: form.region || null,
        service: form.service || "Sonstiges",
        object_type: form.object_type || null,
        area: form.area || null,
        floor: form.floor || null,
        elevator: form.elevator || null,
        timeframe: form.timeframe || null,
        callback_time: form.callback_time || null,
        outside_region: form.outside_region,
        message: form.message,
        consent: form.consent,
        honeypot: form.hp_field,
        files: files.map((f) => ({ filename: f.name, mime_type: f.mime, data_base64: f.base64 })),
      }, { timeout: 90000 });
      if (res?.data?.status !== "success") throw new Error("unexpected response");
      setResult({ ok: true, number: res.data.number || null });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Validierungsfehler (4xx) mit konkreter Meldung anzeigen, sonst Standard-Fehlertext
      const message = status && status < 500 && detail ? formatApiErrorDetail(detail) : ERROR_TEXT;
      setResult({ ok: false, message });
    } finally {
      setLoading(false);
    }
  };

  if (result?.ok) {
    return (
      <div
        className="rounded-sm border border-yellow/60 bg-light-gray p-6 md:p-8"
        role="status"
        aria-live="polite"
        data-testid="cf-success"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-yellow">
            <CheckCircle2 className="h-6 w-6 text-brand-black" />
          </span>
          <div>
            <p className="font-heading text-lg font-bold text-brand-black">Vielen Dank.</p>
            <p className="mt-1 text-base leading-relaxed text-anthracite">
              Ihre Anfrage ist eingegangen. Wir melden uns in der Regel zeitnah telefonisch oder per E-Mail.
            </p>
            {result.number && (
              <p className="mt-3 text-sm text-mid-gray" data-testid="cf-success-number">
                Ihre Anfrage-Nr.: <span className="font-semibold text-brand-black">{result.number}</span>
              </p>
            )}
            <button
              type="button"
              onClick={resetForm}
              className="mt-5 inline-flex items-center gap-2 rounded-sm bg-yellow px-5 py-3 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
              data-testid="cf-new-inquiry"
            >
              <RotateCcw className="h-4 w-4" /> Weitere Anfrage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid={`contact-form-${variant}`} noValidate>
      {/* honeypot */}
      <input
        type="text"
        name="hp_field"
        value={form.hp_field}
        onChange={set("hp_field")}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="cf-name">
            {isBusiness ? "Ansprechpartner" : "Name"} *
          </label>
          <input
            id="cf-name"
            className={inputCls}
            required
            value={form.name}
            onChange={set("name")}
            data-testid="cf-name"
          />
        </div>
        {isBusiness ? (
          <div>
            <label className={labelCls} htmlFor="cf-company">Unternehmen *</label>
            <input
              id="cf-company"
              className={inputCls}
              required
              value={form.company}
              onChange={set("company")}
              data-testid="cf-company"
            />
          </div>
        ) : (
          <div>
            <label className={labelCls} htmlFor="cf-location">Einsatzort *</label>
            <input
              id="cf-location"
              className={inputCls}
              required
              placeholder="z. B. Ulm"
              value={form.location}
              onChange={set("location")}
              data-testid="cf-location"
            />
          </div>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="cf-phone">Telefon</label>
          <input
            id="cf-phone"
            type="tel"
            className={inputCls}
            value={form.phone}
            onChange={set("phone")}
            data-testid="cf-phone"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-email">E-Mail</label>
          <input
            id="cf-email"
            type="email"
            className={inputCls}
            value={form.email}
            onChange={set("email")}
            data-testid="cf-email"
          />
        </div>
      </div>

      {isBusiness && (
        <div>
          <label className={labelCls} htmlFor="cf-location-b">Objektadresse / Einsatzort *</label>
          <input
            id="cf-location-b"
            className={inputCls}
            required
            value={form.location}
            onChange={set("location")}
            data-testid="cf-location-b"
          />
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="cf-service">Gewünschte Leistung *</label>
          <select
            id="cf-service"
            className={inputCls}
            required
            value={form.service}
            onChange={set("service")}
            data-testid="cf-service"
          >
            <option value="">Bitte wählen …</option>
            {serviceOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-area">Ungefähre Fläche</label>
          <input
            id="cf-area"
            className={inputCls}
            placeholder="z. B. 3-Zimmer-Wohnung, 80 m²"
            value={form.area}
            onChange={set("area")}
            data-testid="cf-area"
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="cf-objecttype">Objektart</label>
          <input
            id="cf-objecttype"
            className={inputCls}
            placeholder="z. B. Wohnung, Haus, Keller, Büro"
            value={form.object_type}
            onChange={set("object_type")}
            data-testid="cf-objecttype"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-timeframe">Gewünschter Zeitraum</label>
          <input
            id="cf-timeframe"
            className={inputCls}
            placeholder="z. B. innerhalb von 4 Wochen"
            value={form.timeframe}
            onChange={set("timeframe")}
            data-testid="cf-timeframe"
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="cf-postal">Postleitzahl</label>
          <input id="cf-postal" className={inputCls} value={form.postal_code} onChange={set("postal_code")} data-testid="cf-postal" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-region">Region</label>
          <select id="cf-region" className={inputCls} value={form.region} onChange={set("region")} data-testid="cf-region">
            <option value="">Bitte wählen …</option>
            {regionHubs.map((r) => (
              <option key={r.slug} value={r.name}>{r.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        <div>
          <label className={labelCls} htmlFor="cf-floor">Etage</label>
          <input id="cf-floor" className={inputCls} placeholder="z. B. 2. OG" value={form.floor} onChange={set("floor")} data-testid="cf-floor" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-elevator">Aufzug</label>
          <select id="cf-elevator" className={inputCls} value={form.elevator} onChange={set("elevator")} data-testid="cf-elevator">
            <option value="">Bitte wählen …</option>
            <option value="Ja">Ja</option>
            <option value="Nein">Nein</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-callback">Rückrufzeit</label>
          <input id="cf-callback" className={inputCls} placeholder="z. B. vormittags" value={form.callback_time} onChange={set("callback_time")} data-testid="cf-callback" />
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="cf-message">Nachricht *</label>
        <textarea
          id="cf-message"
          className={`${inputCls} min-h-[140px] resize-y`}
          required
          placeholder="Beschreiben Sie kurz Ihr Anliegen."
          value={form.message}
          onChange={set("message")}
          data-testid="cf-message"
        />
      </div>

      <div>
        <label className={labelCls}>Bilder oder PDF (optional, max. {MAX_FILES})</label>
        <label
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-mid-gray/40 bg-white py-7 text-center transition-colors hover:border-yellow"
          data-testid="cf-upload-label"
        >
          <Upload className="h-6 w-6 text-mid-gray" />
          <span className="text-sm text-mid-gray">Bilder zur ersten Einschätzung senden – JPG, PNG, WEBP oder PDF (max. 6 MB)</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" onChange={onFiles} data-testid="cf-file-input" />
        </label>
        {files.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {files.map((f, i) => (
              <div key={i} className="group relative flex aspect-square items-center justify-center overflow-hidden rounded-sm border border-mid-gray/20 bg-light-gray">
                {f.isPdf ? (
                  <div className="flex flex-col items-center gap-1 p-2 text-center">
                    <FileText className="h-7 w-7 text-mid-gray" />
                    <span className="line-clamp-2 text-[10px] text-mid-gray">{f.name}</span>
                  </div>
                ) : (
                  <img src={`data:${f.mime};base64,${f.base64}`} alt={f.name} className="h-full w-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand-black/80 text-white"
                  aria-label="Datei entfernen"
                  data-testid={`cf-remove-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-mid-gray">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-yellow"
          checked={form.outside_region}
          onChange={set("outside_region")}
          data-testid="cf-outside-region"
        />
        <span>Mein Einsatzort liegt außerhalb der genannten Regionen. (Auch Anfragen aus weiteren Regionen prüfen wir individuell.)</span>
      </label>

      <label className="flex items-start gap-3 text-sm text-mid-gray">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-yellow"
          checked={form.consent}
          onChange={set("consent")}
          data-testid="cf-consent"
        />
        <span>
          Ich habe die Datenschutzhinweise zur Kenntnis genommen und willige in die Verarbeitung
          meiner Angaben zur Bearbeitung meiner Anfrage ein. *
        </span>
      </label>

      {result && !result.ok && (
        <div
          className="flex items-start gap-3 rounded-sm border border-red-300 bg-red-50 p-4 text-base text-red-800"
          role="alert"
          data-testid="cf-error"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{result.message}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark disabled:opacity-60 sm:w-auto"
        data-testid="cf-submit"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Wird gesendet …" : "Anfrage senden"}
      </button>
    </form>
  );
}
